import React from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { I18n as I } from 'react-redux-i18n';
import { ConfirmationModal } from 'vdlib';
import { Button, Tooltip, Row, Col, Descriptions } from 'antd';
import LookupsHelper from 'lib/LookupsHelper';
import Portlet from '../layout/Portlet';
import { Link } from 'react-router';
import { history } from 'data/Store';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';

const baseButtonStyle = {
  backgroundColor: 'white',
  marginRight: 5,
  borderRadius: '50% !important',
};

const descriptionDetailsStyle = {
  fontSize: 16,
  fontWeight: 600,
};

const Garage = ({
  countries,
  deleteGarage,
  fetchGarage,
  lang,
  garage,
  params,
  regions,
  submitting,
  taxOffices,
}) => {
  const [garageIdToDelete, setGarageIdToDelete] = React.useState(null);

  const garageId = React.useMemo(() => {
    try {
      return String(parseInt(params.id)) === String(params.id)
        ? params.id
        : null;
    } catch (e) {
      return null;
    }
  }, [params]);

  React.useEffect(() => {
    if (garageId) fetchGarage(garageId);
  }, [garageId]); //eslint-disable-line

  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (!firstRender.current && !submitting)
      history.push({ pathname: '/all-garages' });
    firstRender.current = false;
  }, [submitting]); //eslint-disable-line

  const { portletActions, portletTitle } = React.useMemo(() => {
    if (!garage) return { portletActions: [], portletTitle: '' };

    return {
      portletTitle: garage.DistinctiveTitle,
      portletActions: [
        <div
          className="btn-group btn-group-devided"
          data-toggle="buttons"
          key="action-group-1"
        >
          <Tooltip title={I.t('EDIT')}>
            <Link to={'/update-garage/' + params.id}>
              <Button
                type="primary"
                shape="circle"
                style={{
                  ...baseButtonStyle,
                  borderColor: 'blue',
                  color: 'blue',
                }}
                icon={<EditOutlined />}
              />
            </Link>
          </Tooltip>
          <Tooltip title={I.t('DELETE')}>
            <Button
              type="primary"
              shape="circle"
              style={{
                ...baseButtonStyle,
                borderColor: 'red',
                color: 'red',
              }}
              icon={<CloseOutlined />}
              onClick={() => setGarageIdToDelete(garage.ID)}
            />
          </Tooltip>
        </div>,
      ],
    };
  }, [garage, params, setGarageIdToDelete, lang]); //eslint-disable-line

  if (!garageId) return null;

  const {
    AFM = '',
    City = '',
    DistinctiveTitle = '',
    Email = '',
    Fax = '',
    KAD = '',
    Phone = '',
    PostalCode = '',
    Street = '',
    StreetNumber = '',
  } = garage || {};

  return (
    <div>
      <Portlet
        key="main"
        icon="icon-settings"
        title={portletTitle}
        actions={portletActions}
      >
        <Row gutter={[10, 10]}>
          <Col className="profile">
            <Descriptions
              className="profile"
              colon={false}
              column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
              size="small"
              bordered
            >
              <Descriptions.Item
                span={2}
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('TITLE')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>{DistinctiveTitle}</span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('TAXNUMBER')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>{AFM}</span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('TAXOFFICE')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>
                  {garage != null
                    ? LookupsHelper.getDescription(
                        taxOffices,
                        garage.TaxOfficeID
                      )
                    : ''}
                </span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('COUNTRY')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>
                  {garage != null
                    ? LookupsHelper.getDescription(countries, garage.CountryID)
                    : ''}
                </span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('REGION')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>
                  {garage != null
                    ? LookupsHelper.getDescription(regions, garage.RegionID)
                    : ''}
                </span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('CITY')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>{City}</span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('STREET')}</span>
                  </div>
                }
              >
                <span
                  style={descriptionDetailsStyle}
                >{`${Street} ${StreetNumber}`}</span>
              </Descriptions.Item>
              <Descriptions.Item
                span={2}
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('POSTALCODE')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>{PostalCode}</span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('PHONE')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>{Phone}</span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('FAX')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>{Fax}</span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('EMAIL')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>{Email}</span>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>{I.t('ACTIVΙΤΥ')}</span>
                  </div>
                }
              >
                <span style={descriptionDetailsStyle}>{KAD}</span>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Portlet>
      <ConfirmationModal
        onHide={() => setGarageIdToDelete(null)}
        show={garageIdToDelete !== null}
        title={I.t('GARAGE_DELETE')}
        onSubmit={() => deleteGarage(garageIdToDelete)}
        loading={submitting}
        promptText={
          <div className="note note-danger">
            {I.t('MODAL_QUESTION_GARAGE_DELETE')}
            <span className="bold">{DistinctiveTitle}</span>
            <br />
            <br />
            {I.t('ARE_YOU_SURE')}
          </div>
        }
      />
    </div>
  );
};

const lookupPropType = arrayOf(
  shape({
    Code: string,
    Description: string,
    ID: number,
  })
);

Garage.propTypes = {
  countries: lookupPropType,
  deleteGarage: func,
  fetchGarage: func,
  garage: shape({
    AFM: string,
    City: string,
    DistinctiveTitle: string,
    Email: string,
    Fax: string,
    KAD: string,
    Phone: string,
    PostalCode: string,
    Street: string,
    StreetNumber: string,
  }),
  params: shape({ id: string }),
  regions: lookupPropType,
  submitting: bool,
  taxOffices: lookupPropType,
};

export default Garage;
